import {
  Button,
  Flex,
  IconEditLine,
  IconPrinterLine,
  IconTrashLine,
  IconXLine,
  Spinner,
  Text,
  View,
} from "@instructure/ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../components/DialogYesNo/DialogYesNo";
import { UserDetailContext } from "../../../utils/Context";
import { printAgenda } from "./printAgendaUtils";

const PublishedAgenda = ({
  agenda,
  planId,
  fileService,
  onDelete,
  onEdit,
  onEditCancel,
  editingOriginalFileName,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [isPrinting, setIsPrinting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editConfirm, setEditConfirm] = useState(false);
  const [cancelEditConfirm, setCancelEditConfirm] = useState(false);

  const beingEdited = editingOriginalFileName === agenda.file_name;

  function canModify(agenda) {
    if (userDetails.isAC()) return true;
    if (userDetails.isPC()) {
      return (
        agenda.updated_by?.toLowerCase() === userDetails.email?.toLowerCase()
      );
    }
    return false;
  }

  async function handlePrint(agenda) {
    setIsPrinting(true);
    try {
      const response = await fileService.getFileContent(
        agenda.file_name,
        "agenda",
        planId
      );
      const rawContent = response?.content || response;
      const parsed =
        typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;
      printAgenda(parsed, { showPublishedFooter: true });
    } catch {
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.fetchError"),
        "error"
      );
    } finally {
      setIsPrinting(false);
    }
  }

  return (
    <View as="div" padding="small" borderWidth="small" borderRadius="medium">
      <Flex wrap="wrap" gap="small">
        <Flex.Item shouldGrow shouldShrink>
          <Flex alignItems="center" gap="small" wrap="wrap">
            <Flex.Item>
              <Text weight="bold">{agenda.agenda_name}</Text>
            </Flex.Item>
            <Flex.Item>
              <Text size="small" color="secondary">
                {agenda.agenda_meeting_date}
              </Text>
            </Flex.Item>
            <Flex.Item>
              <Text size="small" color="secondary">
                {t("assessmentPlanningDashboard.meetingAgenda.itemCount", {
                  count: agenda.agenda_item_count,
                })}
              </Text>
            </Flex.Item>
            <Flex.Item>
              <Text size="x-small" color="secondary">
                {t("assessmentPlanningDashboard.meetingAgenda.publishedOn", {
                  date: new Date(agenda.last_modified).toLocaleString("en-CA", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }),
                })}
              </Text>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Flex wrap="wrap" gap="small">
            <Flex.Item>
              <Button
                size="small"
                onClick={() => handlePrint(agenda)}
                interaction={isPrinting ? "disabled" : "enabled"}
                renderIcon={
                  isPrinting ? (
                    <Spinner
                      size="x-small"
                      renderTitle={t("global.pleaseWait")}
                    />
                  ) : (
                    IconPrinterLine
                  )
                }
              >
                {t("assessmentPlanningDashboard.meetingAgenda.print")}
              </Button>
            </Flex.Item>
            {canModify(agenda) && !beingEdited && (
              <Flex.Item>
                <Button
                  renderIcon={IconEditLine}
                  size="small"
                  onClick={() => setEditConfirm(true)}
                >
                  {t("academicHierarchy.edit")}
                </Button>
              </Flex.Item>
            )}
            {beingEdited && (
              <Flex.Item>
                <Button
                  renderIcon={IconXLine}
                  size="small"
                  onClick={() => setCancelEditConfirm(true)}
                >
                  {t("assessmentPlanningDashboard.meetingAgenda.cancelEdit")}
                </Button>
              </Flex.Item>
            )}
            {canModify(agenda) && !beingEdited && (
              <Flex.Item>
                <Button
                  renderIcon={IconTrashLine}
                  size="small"
                  color="danger"
                  onClick={() => setDeleteConfirm(true)}
                >
                  {t("evidence.delete")}
                </Button>
              </Flex.Item>
            )}
          </Flex>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        openStatus={deleteConfirm}
        message={t("assessmentPlanningDashboard.meetingAgenda.deleteConfirm", {
          name: agenda.agenda_name,
        })}
        onClickYes={async () => {
          await onDelete(agenda);
          setDeleteConfirm(false);
        }}
        onClickNo={() => setDeleteConfirm(false)}
      />

      <DialogYesNo
        openStatus={editConfirm}
        message={t("assessmentPlanningDashboard.meetingAgenda.editConfirm", {
          name: agenda.agenda_name,
        })}
        onClickYes={async () => {
          await onEdit(agenda);
          setEditConfirm(false);
        }}
        onClickNo={() => setEditConfirm(false)}
        shouldReturnFocus={false}
      />
      <DialogYesNo
        openStatus={cancelEditConfirm}
        message={t(
          "assessmentPlanningDashboard.meetingAgenda.cancelEditConfirm"
        )}
        onClickYes={() => {
          onEditCancel();
          setCancelEditConfirm(false);
        }}
        onClickNo={() => setCancelEditConfirm(false)}
      />
    </View>
  );
};

export default PublishedAgenda;
